/**
 * AdoraCare - the intake-spine next-action machine (pure;
 * PATIENT-INTAKE-IDENTITY-AND-AUTOMATIC-CHECKIN-SPINE-V1).
 *
 * ONE-ACTION UI LAW: at each day-board row state the receptionist sees
 * exactly ONE primary action; the system performs the technical
 * transitions. This module is the deterministic verdict the board
 * container/view execute -- no IO, no side effects, trivially testable.
 *
 * FLAG-OFF (or any absent input) every verdict is the neutral no-op, so
 * the V3-P6 identity-step workflow stays byte-identical (the rollback
 * path; test-pinned both ways).
 *
 * The verdicts:
 *   - AUTO CHECK-IN (returning patient): the arrival hop on a LINKED row
 *     composes the two EXISTING writers (arrived -> checked-in) in one
 *     press -- the DOCTOR-ONE-CLOSE client-orchestrator precedent. One
 *     meaningful click; zero new endpoints.
 *   - START/RESUME INTAKE (new patient): the arrived UNLINKED row's one
 *     primary action opens the intake drawer to COMPLETE/RESUME registration;
 *     the stored checked-in hop demotes INTO the drawer's skip door ("Check in
 *     without intake" -- the QUEUE LAW preserved: intake never blocks the
 *     queue). CONSULT-INTAKE-RENDER-HEAL-V1 (W-22, scope 3c): the REVIEW-intake
 *     door is RETIRED from this desk surface -- intake review lives on the
 *     doctor consult surface only; a row whose door would be 'review' falls
 *     back to the normal forward hop here.
 *   - IDENTITY REPAIR (skip-door class): a CHECKED-IN unlinked row keeps
 *     its existing primary (the visit flow) and gains a QUIET intake door
 *     -- never a second primary.
 */

import type { TodayAppointment } from '@/dashboard/types';

/** AUG-12-WALK-2-REGISTRATION-TRUTH-V1 (F3, founder walk W-6): the row verb
 *  names the JOB -- completing the person's registration -- not the internal
 *  session vocabulary. The door opens the Complete Registration modal
 *  (fill-yourself vs we-help); resume re-opens the ONE form. */
export const START_INTAKE_COPY = 'Complete registration';
export const RESUME_INTAKE_COPY = 'Resume registration';
export const REVIEW_INTAKE_COPY = 'Review intake';
export const ARRIVED_AUTO_CHECKIN_SUCCESS_COPY =
  'Marked arrived and checked in - the doctor queue has the patient.';
export const ARRIVED_AUTO_CHECKIN_PARTIAL_COPY =
  'Marked arrived, but check-in did not go through - retry check-in.';

export type IntakeRowFacts = Pick<
  TodayAppointment,
  'status' | 'leadId' | 'patientId' | 'hasPatientRecord' | 'registrationCaptured' | 'intakeStatus' | 'intakeMode'
>;

export interface IntakeRowPlan {
  /** The intake door to render for this row (null = no door). */
  readonly intakeAction: 'start' | 'resume' | 'review' | null;
  readonly intakeActionLabel: string | null;
  /** TRUE when the intake door is the row's ONE primary action
   *  (arrived + unlinked); FALSE renders it as a quiet secondary
   *  (checked-in identity repair). */
  readonly intakeIsPrimary: boolean;
  /** TRUE when the stored checked-in hop should NOT render on the row
   *  (it demotes into the drawer's skip door -- one primary, no wall). */
  readonly suppressCheckedInAction: boolean;
}

const NO_PLAN: IntakeRowPlan = {
  intakeAction: null,
  intakeActionLabel: null,
  intakeIsPrimary: false,
  suppressCheckedInAction: false,
};

function isUnlinkedLeadRow(row: IntakeRowFacts): boolean {
  // patientId may be inherited from the lead before this appointment has
  // passed its checkpoint. It is not evidence that check-in is authorized.
  return row.leadId != null && row.hasPatientRecord !== true && row.registrationCaptured !== true;
}

function doorForIntakeState(
  row: IntakeRowFacts,
): { action: 'start' | 'resume' | 'review'; label: string } {
  if (row.intakeStatus === 'in_progress') {
    return row.intakeMode === 'paper'
      ? { action: 'review', label: REVIEW_INTAKE_COPY }
      : { action: 'resume', label: RESUME_INTAKE_COPY };
  }
  if (row.intakeStatus === 'completed') {
    // T9a (IDENTITY-LINK-AT-INTAKE-V1): a row whose intake session is
    // COMPLETED (patient_intake_sessions.status = 'completed', derived onto
    // the board wire as intakeStatus) renders the EXISTING review verb --
    // never a second "Start intake" that could walk a finished intake back
    // through creation. The existing UMR is preserved (the review view
    // writes nothing).
    return { action: 'review', label: REVIEW_INTAKE_COPY };
  }
  return { action: 'start', label: START_INTAKE_COPY };
}

/** The row's intake-door verdict. Pure; flag-off = the neutral no-op. */
export function intakeRowPlan(
  row: IntakeRowFacts,
  intakeSpineEnabled: boolean,
): IntakeRowPlan {
  if (!intakeSpineEnabled) return NO_PLAN;
  if (!isUnlinkedLeadRow(row)) return NO_PLAN;
  if (row.status !== 'arrived' && row.status !== 'checked_in') return NO_PLAN;
  const door = doorForIntakeState(row);
  // CONSULT-INTAKE-RENDER-HEAL-V1 (W-22, scope 3c): the Review-intake action is
  // REMOVED from the front-desk day-board row -- intake review lives on the
  // doctor consult surface only. A completed / paper-in-progress intake row
  // (whose door would be 'review') therefore yields NO intake door here and
  // falls back to the row's normal forward hop (Mark checked in); it no longer
  // claims the primary or suppresses the check-in hop. Complete / Resume
  // registration (the desk's own registration-completion job) are untouched.
  if (door.action === 'review') return NO_PLAN;
  if (row.status === 'arrived') {
    return {
      intakeAction: door.action,
      intakeActionLabel: door.label,
      intakeIsPrimary: true,
      suppressCheckedInAction: true,
    };
  }
  // Skip-door class (checked in without intake): identity repair stays
  // available as a QUIET door; the visit flow keeps the primary.
  return {
    intakeAction: door.action,
    intakeActionLabel: door.label,
    intakeIsPrimary: false,
    suppressCheckedInAction: false,
  };
}

/** TRUE when the arrival hop should compose arrived -> checked-in in one
 *  press (returning patient: confidently linked, no required update owner
 *  exists in repo truth -- the named seam). Pure; flag-off = false. */
export function autoCheckInOnArrival(
  row: IntakeRowFacts,
  intakeSpineEnabled: boolean,
): boolean {
  return (
    intakeSpineEnabled &&
    row.hasPatientRecord === true
  );
}

// --- The one-press composition (deterministic; the container maps outcomes
// to feedback copy) ----------------------------------------------------------

export interface ArriveCheckInMutations {
  readonly arrived: (id: number) => Promise<unknown>;
  readonly checkedIn: (id: number) => Promise<unknown>;
}

export type ArriveAndCheckInOutcome =
  /** both existing writers committed -- the doctor queue has the patient */
  | 'checked_in'
  /** arrival committed, check-in refused/failed -- the row re-derives as
   *  arrived and keeps its manual hop (honest partial, never silent) */
  | 'arrived_only'
  /** the arrival itself failed -- nothing moved */
  | 'failed';

/** Compose the two EXISTING writers sequentially (the DOCTOR-ONE-CLOSE
 *  client-orchestrator precedent). Never throws; the outcome names exactly
 *  what committed. */
export async function composeArriveAndCheckIn(
  mutations: ArriveCheckInMutations,
  appointmentId: number,
): Promise<ArriveAndCheckInOutcome> {
  try {
    await mutations.arrived(appointmentId);
  } catch {
    return 'failed';
  }
  try {
    await mutations.checkedIn(appointmentId);
    return 'checked_in';
  } catch {
    return 'arrived_only';
  }
}
